import React, { useCallback, useEffect, useState } from "react";
import { LayoutTemplate, Plus, Pencil, History } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import StatusSelectField, { FIVE_STATE_STATUS_VALUES } from "../components/StatusSelectField";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { createLandingPage, createLandingPageVersion, getLandingPages, updateLandingPageMetadata } from "../../../lib/emailMarketingApi";
import { api } from "../../../lib/api";

/**
 * LandingPagesPanel — EMAILS-IMPL-009
 *
 * Landing Page (LPG) is the one Asset family with no canonical content
 * field at all (LPG-SPEC-001 has no structured "Destination URL" section
 * on the real runtime model -- confirmed during EMAILS-IMPL-008's own
 * governance re-read, reconfirmed here; none is fabricated). The only
 * authoring surface is a fully OPTIONAL internal reference
 * (internal_landing_page_id + internal_landing_page_version) into this
 * codebase's OWN internal Landing Page Builder domain
 * (app/routers/landing_pages.py) -- both fields together, or neither; the
 * backend rejects one without the other.
 *
 * Per checkpoint §15, this prefers "select a real internal Landing Page ->
 * version" over free-text internal IDs: `GET /landing-pages/list` and
 * `GET /landing-pages/{id}/versions` (that router's own pre-existing read
 * endpoints, gated by `landing_pages: full` -- a DIFFERENT module than
 * `emails`) populate two cascading Selects. This is a genuine cross-module
 * RBAC coupling, same as EMAILS-IMPL-007's own accepted precedent of
 * Program/Campaign/Angle/Audience Definition reading through a
 * War-Room-gated endpoint -- unlike THIS checkpoint's own Asset-catalog
 * list (which must not depend on War Room, per §25), this one is directly
 * sanctioned by name in §15 ("If MissionAssist has an existing Landing
 * Page list/read endpoint... use it"). If the operator's account lacks
 * `landing_pages` access, the list call 403s and this component degrades
 * to a disabled selector with an inline explanation -- it never blocks
 * creating an LPG Asset with no internal reference at all.
 *
 * The canonical historical LPG0001 has no proven crosswalk into this
 * internal domain (EMAILS-IMPL-008's own characterization) -- nothing here
 * implies one; a fresh LPG Asset created here starts with no reference
 * until an operator explicitly picks one.
 */
const STATUS_VALUES = FIVE_STATE_STATUS_VALUES;
const NO_REFERENCE = "__none__";

function emptyForm() {
  return { name: "", status: STATUS_VALUES[0], landingPageId: NO_REFERENCE, versionNumber: "" };
}

function useInternalLandingPages() {
  const [landingPages, setLandingPages] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | unavailable

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/landing-pages/list");
        if (!cancelled) {
          setLandingPages(res.data?.items || []);
          setStatus("ready");
        }
      } catch {
        // 403 (no landing_pages access) or any other failure -- degrade,
        // never block LPG Asset creation over it.
        if (!cancelled) setStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { landingPages, status };
}

function useInternalLandingPageVersions(landingPageId) {
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (!landingPageId || landingPageId === NO_REFERENCE) {
      setVersions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/landing-pages/${encodeURIComponent(landingPageId)}/versions`);
        if (!cancelled) setVersions(res.data?.versions || []);
      } catch {
        if (!cancelled) setVersions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [landingPageId]);

  return versions;
}

function InternalReferenceFields({ form, setForm, testPrefix }) {
  const { landingPages, status } = useInternalLandingPages();
  const versions = useInternalLandingPageVersions(form.landingPageId);

  return (
    <div className="space-y-3 rounded-md border border-white/10 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Internal Landing Page Reference (optional)</div>
      {status === "unavailable" && (
        <p className="text-xs text-amber-300" data-testid={`${testPrefix}-lp-unavailable`}>
          Unable to load internal Landing Pages (requires Landing Pages access). You can still create this Landing Page Asset without an internal reference.
        </p>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={`${testPrefix}-lp-select`}>Internal Landing Page</Label>
        <Select
          value={form.landingPageId}
          onValueChange={(landingPageId) => setForm((f) => ({ ...f, landingPageId, versionNumber: "" }))}
          disabled={status !== "ready"}
        >
          <SelectTrigger id={`${testPrefix}-lp-select`} data-testid={`${testPrefix}-lp-select`}>
            <SelectValue placeholder={status === "loading" ? "Loading…" : "No internal reference"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_REFERENCE} data-testid={`${testPrefix}-lp-option-none`}>No internal reference</SelectItem>
            {landingPages.map((lp) => (
              <SelectItem key={lp.id} value={lp.id} data-testid={`${testPrefix}-lp-option-${lp.id}`}>
                {lp.name} ({lp.id})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {form.landingPageId !== NO_REFERENCE && (
        <div className="space-y-1.5">
          <Label htmlFor={`${testPrefix}-lp-version-select`}>Internal Landing Page Version</Label>
          <Select
            value={form.versionNumber ? String(form.versionNumber) : ""}
            onValueChange={(versionNumber) => setForm((f) => ({ ...f, versionNumber }))}
          >
            <SelectTrigger id={`${testPrefix}-lp-version-select`} data-testid={`${testPrefix}-lp-version-select`}>
              <SelectValue placeholder="Select a version" />
            </SelectTrigger>
            <SelectContent>
              {versions.map((v) => (
                <SelectItem key={v.id} value={String(v.version_number)} data-testid={`${testPrefix}-lp-version-option-${v.version_number}`}>
                  v{v.version_number}{v.is_active ? " (active)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

export default function LandingPagesPanel() {
  // See AssetFamilyPanel.jsx's own comment: these must be stable
  // identities (useCallback, not fresh inline closures) or `refresh`
  // retriggers its own effect every render.
  //
  // EMAILS-IMPL-009A: `list` now calls the real persistent catalog
  // (getLandingPages()) instead of EMAILS-IMPL-009's `async () => []`
  // stub.
  const listFn = useCallback(() => getLandingPages(), []);
  const createFn = useCallback(
    ({ name, status, landingPageId, versionNumber }) =>
      createLandingPage({
        name,
        status,
        internalLandingPageId: landingPageId === NO_REFERENCE ? null : landingPageId,
        internalLandingPageVersion: landingPageId === NO_REFERENCE ? null : Number(versionNumber),
      }),
    []
  );
  const updateFn = useCallback((id, { name, status }) => updateLandingPageMetadata(id, { name, status }), []);
  const createVersionFn = useCallback(
    (id, { version, landingPageId, versionNumber }) =>
      createLandingPageVersion(id, {
        version,
        internalLandingPageId: landingPageId === NO_REFERENCE ? null : landingPageId,
        internalLandingPageVersion: landingPageId === NO_REFERENCE ? null : Number(versionNumber),
      }),
    []
  );
  const getIdFn = useCallback((row) => row.landing_page_id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem, createVersionItem } =
    useEmailCatalogEntityList({
      list: listFn,
      create: createFn,
      update: updateFn,
      createVersion: createVersionFn,
      getId: getIdFn,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", status: STATUS_VALUES[0] });
  const [versioningRow, setVersioningRow] = useState(null);
  const [versionForm, setVersionForm] = useState({ version: "", landingPageId: NO_REFERENCE, versionNumber: "" });

  const openCreate = () => {
    setCreateForm(emptyForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status, landingPageId: createForm.landingPageId, versionNumber: createForm.versionNumber });
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced; keep dialog open
    }
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name, status: row.status });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingRow.landing_page_id, {
        name: editForm.name.trim() !== editingRow.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingRow.status ? editForm.status : undefined,
      });
      setEditingRow(null);
    } catch {
      // dialog stays open
    }
  };

  const openVersion = (row) => {
    setVersioningRow(row);
    setVersionForm({
      version: "",
      landingPageId: row.internal_landing_page_id || NO_REFERENCE,
      versionNumber: row.internal_landing_page_version ? String(row.internal_landing_page_version) : "",
    });
    clearMutationError();
  };

  const submitVersion = async () => {
    try {
      await createVersionItem(versioningRow.landing_page_id, {
        version: versionForm.version.trim(),
        landingPageId: versionForm.landingPageId,
        versionNumber: versionForm.versionNumber,
      });
      setVersioningRow(null);
    } catch {
      // dialog stays open (e.g. 409 version conflict, 422 dangling reference)
    }
  };

  if (loading) return <CatalogLoadingCard testId="landing-pages-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="landing-pages-error" />;

  return (
    <div className="space-y-4" data-testid="landing-pages-panel">
      <CatalogTable
        testId="landing-pages-table"
        title="Landing Pages"
        icon={LayoutTemplate}
        headerAction={
          <Button type="button" size="sm" className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500" onClick={openCreate} data-testid="new-landing-pages-button">
            <Plus className="h-4 w-4" /> New Landing Page
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.landing_page_id}
        onRowClick={(row) => setExpandedId(expandedId === row.landing_page_id ? null : row.landing_page_id)}
        emptyTitle="No Landing Pages yet"
        emptySubtitle='Create one with "New Landing Page" to get started.'
        columns={[
          {
            key: "name",
            label: "Landing Page",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.landing_page_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`landing-pages-status-${row.landing_page_id}`} /> },
          { key: "current_version", label: "Current Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.current_version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.landing_page_id === expandedId)
        .map((row) => (
          <div key={row.landing_page_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`landing-pages-detail-${row.landing_page_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Detail label="Governed ID" value={row.landing_page_id} mono />
                <Detail label="Status" value={row.status || "—"} />
                <Detail label="Current Version" value={row.current_version || "—"} mono />
                <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
                <div className="col-span-2 sm:col-span-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Internal Reference</div>
                  <div className="mt-1 text-slate-200" data-testid={`landing-pages-reference-${row.landing_page_id}`}>
                    {row.internal_landing_page_id
                      ? `${row.internal_landing_page_id} — v${row.internal_landing_page_version}`
                      : "No internal reference"}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/10 text-slate-200" onClick={() => openEdit(row)} data-testid={`edit-landing-pages-${row.landing_page_id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Metadata
                </Button>
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-violet-400/30 text-violet-200" onClick={() => openVersion(row)} data-testid={`new-version-landing-pages-${row.landing_page_id}`}>
                  <History className="h-3.5 w-3.5" /> Create New Version
                </Button>
              </div>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-landing-pages-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Landing Page"
        description="A governed Landing Page Asset (LPG####), with an optional reference to a real internal Landing Page. No external destination URL field exists on this Asset."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Landing Page"
      >
        <div className="space-y-2">
          <Label htmlFor="landing-pages-name">Name</Label>
          <Input id="landing-pages-name" value={createForm.name} onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))} required data-testid="landing-pages-name-input" />
        </div>
        <StatusSelectField id="landing-pages-status" value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="landing-pages-status-select" />
        <InternalReferenceFields form={createForm} setForm={setCreateForm} testPrefix="landing-pages" />
      </EntityFormDialog>

      {editingRow && (
        <EntityFormDialog
          testId="edit-landing-pages-dialog"
          open={!!editingRow}
          onOpenChange={(open) => !open && setEditingRow(null)}
          title={`Edit ${editingRow.landing_page_id}`}
          description='Rename or change the governed status of this Landing Page Asset. Use "Create New Version" to change the internal reference.'
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="landing-pages-edit-name">Name</Label>
            <Input id="landing-pages-edit-name" value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} required data-testid="landing-pages-edit-name-input" />
          </div>
          <StatusSelectField id="landing-pages-edit-status" value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} values={STATUS_VALUES} testId="landing-pages-edit-status-select" />
        </EntityFormDialog>
      )}

      {versioningRow && (
        <EntityFormDialog
          testId="new-version-landing-pages-dialog"
          open={!!versioningRow}
          onOpenChange={(open) => !open && setVersioningRow(null)}
          title={`New Version — ${versioningRow.landing_page_id}`}
          description="Creates a new, immutable Version. The current Version stays historically accessible and unchanged."
          onSubmit={submitVersion}
          submitting={mutating}
          error={mutationError}
          submitLabel="Create New Version"
        >
          <div className="rounded-md border border-white/10 bg-black/20 p-3 text-sm">
            <div className="flex justify-between text-slate-400">
              <span>Asset</span>
              <span className="font-mono text-slate-200" data-testid="new-version-landing-pages-asset-id">{versioningRow.landing_page_id}</span>
            </div>
            <div className="mt-1 flex justify-between text-slate-400">
              <span>Current version</span>
              <span className="font-mono text-slate-200" data-testid="new-version-landing-pages-current">{versioningRow.current_version || "—"}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="landing-pages-new-version">New Version</Label>
            <Input id="landing-pages-new-version" value={versionForm.version} onChange={(e) => setVersionForm((f) => ({ ...f, version: e.target.value }))} placeholder="e.g. 2.0.0" required data-testid="landing-pages-new-version-input" />
          </div>
          <InternalReferenceFields form={versionForm} setForm={setVersionForm} testPrefix="landing-pages-new" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
